// Every per-project setting across the projects, in the sections and order a project's
// Settings tab uses, with what switching each on costs
// (docs/Y2026-Q4/design-switch-on-cost-warnings.md).
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import {
  type TeamSettings,
  useSetEnrolment,
  useSetProjectSwitch,
  useTeamSettings,
} from "../api/hooks/useAgentPortfolio";
import { ProjectEnablementList } from "../components/ProjectEnablementList";
import { SettingSubcard } from "../components/SettingSubcard";
import { SettingsGroup } from "../components/SettingsGroup";
import { FEATURE_FLAGS, SECTION_TITLES, type FeatureFlag } from "../components/settingsSections";
import { specialistWarning, usdRange } from "../components/switchCostWarnings";
import { perTick } from "../utils/projectEnablement";
import { TeamAgentConfigsCard } from "./TeamAgentConfigsCard";
import { TeamScheduledRuns } from "./TeamScheduledRuns";
import styles from "./TeamSettingsTab.module.css";

const ALL_ON = "Enabled for every project.";

const joined = (labels: string[]) =>
  labels.length > 1
    ? `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`
    : labels.join("");

interface Row {
  project_id: string;
  project_name: string;
  on: boolean;
  /** What switching it on here costs; null for no figure. */
  usd: number | null;
}

export const TeamSettingsTab = () => {
  const { data, isLoading, error } = useTeamSettings();
  if (isLoading) return <p className={styles.muted}>Loading…</p>;
  if (error || !data) {
    return <p className={styles.error}>Could not load the team&apos;s settings.</p>;
  }
  const costs = data.switch_costs;
  // A project the nightly run skips has no specialist work to switch on.
  const running = data.all_projects.filter((p) => p.scheduled_runs);
  const names = new Map(running.map((p) => [p.project_id, p.project_name]));
  return (
    <div className={styles.page}>
      <p className={styles.muted}>Each project setting, across every project.</p>
      <SettingsGroup title={SECTION_TITLES.runs}>
        <TeamScheduledRuns
          alwaysOn={joined(data.always_on.map((a) => a.label))}
          projects={data.all_projects}
          switchCosts={costs}
        />
      </SettingsGroup>
      <SettingsGroup title={SECTION_TITLES.specialists}>
        {data.specialists.map((specialist) => (
          <SpecialistCard
            key={specialist.agent}
            specialist={specialist}
            switchCosts={costs}
            rows={specialist.all_projects.flatMap((p) => {
              const name = names.get(p.project_id);
              return name === undefined
                ? []
                : [{ project_id: p.project_id, project_name: name, on: p.enrolled, usd: p.first_pass_usd }];
            })}
          />
        ))}
      </SettingsGroup>
      <SettingsGroup title={SECTION_TITLES.flags}>
        {FEATURE_FLAGS.map((flag) => (
          <FlagCard key={flag.column} flag={flag} data={data} />
        ))}
      </SettingsGroup>
      <TeamAgentConfigsCard projects={running} />
    </div>
  );
};

const SpecialistCard = ({
  specialist,
  rows,
  switchCosts,
}: {
  specialist: TeamSettings["specialists"][number];
  rows: Row[];
  switchCosts: TeamSettings["switch_costs"];
}) => {
  const { user } = useKindeAuth();
  const mutation = useSetEnrolment(specialist.agent);
  const costs = rows.flatMap((r) => (r.on || r.usd === null ? [] : [r.usd]));
  return (
    <SettingSubcard
      title={specialist.label}
      describe={specialist.describe}
      warning={costs.length > 0 ? specialistWarning(switchCosts, usdRange(costs)) : null}
    >
      <ProjectEnablementList
        rows={rows.map((r) => ({
          ...r,
          detail: !r.on && r.usd !== null && (
            <span className={styles.cost}>~${r.usd} first run</span>
          ),
        }))}
        saving={perTick(mutation)}
        offHeading="Disabled"
        allOn="Enabled for every project with scheduled runs."
        onChange={(project_id, on) =>
          mutation.mutate({ project_id, enrolled: on, actor_email: user?.email ?? "" })
        }
      />
    </SettingSubcard>
  );
};

const FlagCard = ({ flag, data }: { flag: FeatureFlag; data: TeamSettings }) => {
  const mutation = useSetProjectSwitch(flag.column);
  return (
    <SettingSubcard
      title={flag.title}
      describe={flag.describe}
      warning={flag.warning(data.switch_costs)}
    >
      <ProjectEnablementList
        rows={data.all_projects.map((p) => ({
          project_id: p.project_id,
          project_name: p.project_name,
          on: p[flag.column],
        }))}
        saving={perTick(mutation)}
        offHeading="Disabled"
        allOn={ALL_ON}
        onChange={(project_id, enabled) => mutation.mutate({ project_id, enabled })}
      />
    </SettingSubcard>
  );
};
